import { useIsomorphicLayoutEffect } from '@cian/react-utils';

import { type RefObject, useState } from 'react';

export type TUseBannerOverlayParams = {
  rootRef: RefObject<HTMLDivElement | null>;
  /** Создавать overlay-контейнер только когда есть что в него рендерить. */
  enabled: boolean;
  /** Порядок наложения относительно других overlay внутри баннера. */
  zIndex: number;
  /**
   * Размещение overlay внутри рутового div:
   * - `fill` (по умолчанию) — на весь баннер (`inset: 0`), для шаблонов поверх креатива;
   * - `bottom` — только нижняя полоса (`bottom: 0`, авто-высота), для маркировки/бегущей строки,
   *   чтобы не накрывать креатив и не перехватывать клики.
   */
  placement?: 'fill' | 'bottom';
};

/**
 * Создаёт overlay-контейнер внутри рутового div баннера, в который через портал
 * рендерится произвольный контент (шаблон, скелетон и т.п.).
 *
 * Контейнер добавляется к уже существующей разметке (appendChild), поэтому НЕ
 * перезаписывает то, что вставила в этот же div библиотека adfox. Контент при этом
 * располагается поверх всего блока (position: absolute + inset: 0).
 */
export function useBannerOverlay({
  rootRef,
  enabled,
  zIndex,
  placement = 'fill',
}: TUseBannerOverlayParams): HTMLDivElement | null {
  const [overlayElement, setOverlayElement] = useState<HTMLDivElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const rootElement = rootRef.current;

    if (!enabled || !rootElement) {
      return undefined;
    }

    rootElement.style.position = 'relative';

    const overlay = document.createElement('div');

    overlay.style.position = 'absolute';
    overlay.style.zIndex = String(zIndex);

    if (placement === 'bottom') {
      // Нижняя полоса с авто-высотой — overlay занимает только место под контентом.
      overlay.style.left = '0';
      overlay.style.right = '0';
      overlay.style.bottom = '0';
    } else {
      overlay.style.inset = '0';
    }

    rootElement.appendChild(overlay);
    setOverlayElement(overlay);

    return (): void => {
      overlay.remove();
      setOverlayElement(null);
    };
  }, [rootRef, enabled, zIndex, placement]);

  return overlayElement;
}
